/**
 * Whether this app is running on its own backend or through somebody's Mac.
 *
 * One place, because it is asked from several: the screens want to say it,
 * the wiring wants to be turned on and off by it, and a channel that dies
 * has to be able to tell everybody at once.
 *
 * Deliberately not a React context. The axios adapter and the replaced fetch
 * are global, so the fact behind them is global too, and pretending otherwise
 * would mean a component tree deciding what the network does.
 */

import { noteConnection } from "./connection-log";
import { startHeartbeat } from "./heartbeat";
import { watchForReturn } from "./reconnect";
import { GUEST, keepAppointment, readAppointmentFor } from "./rendezvous";
import { encodeFrame } from "./tunnel-protocol";
import { createTunnelClient } from "./tunnel-client";
import { sendAppCallsThrough } from "./tunnel-wiring";

/** away: on our own backend. live: through the channel. lost: it went away. */
export const AWAY = "away";
export const LIVE = "live";
export const LOST = "lost";
/** The channel went away and this phone is quietly getting it back. */
export const RETURNING = "returning";

const watchers = new Set();
let state = { where: AWAY, since: null, reason: null };
let client = null;
let undo = null;

function announce(next) {
  state = { ...state, ...next };
  watchers.forEach((watcher) => watcher(state));
}

/** What is true right now. */
export function tunnelState() {
  return state;
}

/** Told whenever that changes. Returns the way to stop being told. */
export function watchTunnel(watcher) {
  watchers.add(watcher);
  watcher(state);
  return () => watchers.delete(watcher);
}

/**
 * Start sending this app's calls through the channel.
 *
 * @param channel  something with send(text) and onMessage(handler)
 * @param now      injectable clock, so a test is not at the mercy of one
 */
export function tunnelThrough(
  channel,
  { now = () => Date.now(), onPairing = null, onRendezvous = null, onPong = null, ...wiring } = {}
) {
  stopTunnel();
  client = createTunnelClient({ channel, onPairing, onRendezvous, onPong });
  undo = sendAppCallsThrough(client, wiring);
  announce({ where: LIVE, since: now(), reason: null });
  return client;
}

/**
 * The channel went away.
 *
 * Everything still waiting is failed rather than left hanging, and the app
 * goes back to its own backend, which on a phone means nothing answers. That
 * is the honest state: better a screen that says so than one that spins.
 */
export function tunnelLost(reason = "De verbinding met je Mac is weg.") {
  if (!client) return;
  client.stop(reason);
  undo?.();
  client = null;
  undo = null;
  announce({ where: LOST, reason });
}

/**
 * Put everything back as it was, quietly. For leaving a screen.
 *
 * Also clears a loss that was being shown: leaving the screen is the user
 * saying they have read it, and a stale "your Mac is gone" on a later visit
 * is worse than nothing.
 */
export function stopTunnel() {
  client?.stop("Deze verbinding is gesloten.");
  undo?.();
  client = null;
  undo = null;
  if (state.where !== AWAY) announce({ where: AWAY, since: null, reason: null });
}

/** The live one, for a screen that wants to show what is in flight. */
export function tunnelClient() {
  return client;
}

// ---------------------------------------------------------------------------
// Holding on to it
//
// Everything above is one connection. What follows is the phone keeping one,
// across a locked screen and a suspended tab, which is a different job and the
// reason this file gained a second half on 28 July.
// ---------------------------------------------------------------------------

let held = null;
let beating = null;
let returning = null;
let awake = null;
// Fix afscheid: de luisteraar die bij weggaan doei zegt tegen de Mac.
let afscheid = null;

/**
 * Take this channel and keep it: serve the app through it, watch it, and get
 * it back without asking when it dies.
 *
 * Lives here, at module level, because the phone navigates away from the page
 * that opened the connection the moment it is up. Anything owned by that page
 * would be unmounted three seconds later, which is what made a dropped channel
 * final: nothing was left running to notice.
 */
export function holdTunnel(
  opened,
  {
    onPairing = null,
    note = noteConnection,
    heartbeatImpl = startHeartbeat,
    watchImpl = watchForReturn,
    keepImpl = keepAppointment,
    visible = () => typeof document === "undefined" || document.visibilityState !== "hidden",
    ...wiring
  } = {}
) {
  held = opened;
  beating?.stop();

  const created = tunnelThrough(opened.channel, {
    ...wiring,
    onPairing,
    onRendezvous: (appointment) => {
      // The desktop naming the next meeting place. Written down straight away:
      // it is only useful if it was stored before the channel that carried it
      // stopped working.
      //
      // Stored as the guest's, which is not bookkeeping. The desktop now stands
      // in any appointment it finds; if this phone's copy were indistinguishable
      // from a desktop's, a phone reading it would open the room as host and
      // block the door it was walking through.
      if (keepImpl({ ...appointment, side: GUEST })) note("appointment-kept", "");
    },
    onPong: (id) => beating?.heard(id),
  });
  note("channel-open", "");

  beating = heartbeatImpl({
    visible,
    send: (id) => opened.channel.send(encodeFrame({ kind: "ping", id })),
    onDead: () => {
      note("ping-late", "twee keer geen antwoord");
      loseAndReturn("Je Mac antwoordt niet meer.");
    },
  });

  // One ping now, not in twenty five seconds.
  //
  // The desktop's silence watchdog only judges a phone that has ever pinged,
  // and rightly: an older phone that cannot ping must not be hung up on for
  // being quiet. But the heartbeat's first beat is a whole interval away, so a
  // phone paired and put down inside twenty five seconds never pinged once, and
  // the watchdog stayed switched off for the entire life of that connection.
  // That is exactly what a QR scan at half past one at night looks like: scan,
  // look, put it down. Measured on 28 July; the Mac never noticed the channel
  // die and the phone came back to an empty room.
  //
  // Guarded on the channel actually being open, because `beat` treats a send
  // that throws as proof of death, and doing that to a channel one line old
  // would be a worse bug than the one this fixes.
  if (opened.channel?.ready !== false) beating?.beat?.();

  if (!returning) {
    returning = watchImpl({
      onChannel: (again) => {
        // Straight back into this same function, so a connection recovered at
        // seven in the morning is held exactly like the one made last night.
        holdTunnel(again, { onPairing: null, note, heartbeatImpl, watchImpl, keepImpl, visible, ...wiring });
        returning?.settled();
      },
      onGaveUp: (why) => {
        // Now it is the owner's problem, and only now. Until this point a
        // dead channel was a thing being dealt with, not a thing to report.
        announce({ where: LOST, reason: why });
      },
      note,
    });
  }

  // Waking up is the moment to find out, not twenty-five seconds later.
  //
  // A phone that was suspended comes back believing its channel is fine: its
  // JavaScript was frozen, so it never saw anything close. Left alone it would
  // discover the truth on the next heartbeat cycle, or worse, when the owner's
  // first tap sat there for thirty seconds and failed. One ping on the way in
  // turns that into a couple of seconds.
  if (!awake && typeof document !== "undefined") {
    awake = () => {
      if (document.visibilityState === "hidden") return;
      beating?.beat();
    };
    document.addEventListener("visibilitychange", awake);
  }

  // FIX AFSCHEID (15 oktober 2026). Ververst of sluit de telefoon, dan sluit
  // Safari de directe verbinding niet altijd netjes, en merkte de Mac het
  // vertrek pas na 47 s aan de stilte (gemeten op Franks iPhone, 18:24:10).
  // Al die tijd stond hij niet in de afgesproken kamer en kwam de telefoon op
  // het QR-scherm. Nu zegt de telefoon doei en gaat de Mac meteen naar de
  // afspraak. Altijd over het kanaal dat NU wordt vastgehouden.
  if (!afscheid && typeof window !== "undefined") {
    afscheid = () => {
      try {
        held?.channel?.send(encodeFrame({ kind: "bye", id: "bye" }));
      } catch {
        // Een kanaal dat al dicht is, hoeft niets meer te horen.
      }
    };
    window.addEventListener("pagehide", afscheid);
  }

  opened.channel.onClosed?.(() => loseAndReturn());
  return created;
}

/**
 * The channel is gone. Say so, and start getting it back.
 *
 * The state goes to `returning` rather than `lost`, because on this phone
 * those are different things and only one of them is the owner's problem. It
 * becomes `lost` when the attempt to return gives up.
 */
function loseAndReturn(reason = "De verbinding met je Mac is weg.") {
  if (!client && state.where === RETURNING) return;
  noteConnection("channel-closed", reason);
  client?.stop(reason);
  undo?.();
  client = null;
  undo = null;
  beating?.stop();
  beating = null;
  announce({ where: RETURNING, reason });
  returning?.lost();
}

/** Stop trying, and stop holding. For signing out and for leaving. */
export function releaseTunnel() {
  returning?.stop();
  returning = null;
  beating?.stop();
  beating = null;
  if (awake && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", awake);
  }
  awake = null;
  if (afscheid && typeof window !== "undefined") {
    window.removeEventListener("pagehide", afscheid);
  }
  afscheid = null;
  try {
    held?.close();
  } catch {
    // already gone
  }
  held = null;
  stopTunnel();
}

/**
 * PUNT 1, 12 OKTOBER 2026: NA VERVERSEN OF SLUITEN TERUG ZONDER SCAN.
 *
 * Glasronde v0.9.150: de verversknop in de PWA verbrak de verbinding en de QR
 * moest opnieuw. Gemeten in de code: de afspraak voor de vervolgkamer stond wel
 * in localStorage (`rendezvous.js`, kant `guest`), en de tokens ernaast ook,
 * maar de weg terug startte alleen na een kanaal dat VERLOREN ging. Een pagina
 * die opnieuw laadt verliest niets; zij begint zonder kanaal, en niemand keek
 * dan naar de afspraak.
 *
 * Dit is dezelfde lus als na een vergrendeld scherm (`watchForReturn`), alleen
 * begonnen bij het opstarten. Op de Mac staat geen afspraak van de kant
 * `guest`, dus daar doet dit niets.
 *
 * Geeft een belofte: true zodra het kanaal er weer is, false als er geen
 * afspraak is of de lus het opgeeft.
 */
export function returnOnStart({
  read = () => readAppointmentFor(GUEST),
  note = noteConnection,
  watchImpl = watchForReturn,
  holdImpl = holdTunnel,
} = {}) {
  if (held || state.where === LIVE || !read()) return Promise.resolve(false);
  note("resume-start", "afspraak bewaard");
  announce({ where: RETURNING, reason: null });
  return new Promise((resolve) => {
    let done = false;
    const finish = (value) => {
      if (done) return;
      done = true;
      resolve(value);
    };
    returning?.stop();
    returning = watchImpl({
      onChannel: (again) => {
        holdImpl(again, { onPairing: null, note });
        returning?.settled();
        finish(true);
      },
      onGaveUp: (why) => {
        announce({ where: LOST, reason: why });
        finish(false);
      },
      note,
    });
    returning.lost();
  });
}

let startup = null;

/** Een keer per paginalading, gedeeld door wie erop wacht (AuthContext). */
export function startupReturn(options) {
  if (!startup) startup = returnOnStart(options);
  return startup;
}

/** Voor een toets die met een schone lading wil beginnen. */
export function resetStartupReturn() {
  startup = null;
}

/** The reconnect loop, for a test that wants to drive it. */
export function returnWatcher() {
  return returning;
}
